<template>
  <div>
    <div class="d-flex align-items-center mb-4">
      <router-link :to="assignmentPage" class="mr-2">
        <a class="back-to">
          <mdicon name="ChevronLeft" />
        </a>
      </router-link>
      <h3 class="m-0">Prices for assignment {{ assignment?.reference }}</h3>
    </div>
    <div class="d-flex justify-content-between mb-4">
      <StripeBtn @click="openModal(modalTitle)"> Available carriers </StripeBtn>
      <div class="d-flex align-items-center justify-content-end">
        <span class="d-inline-block mr-2">Reset everything to default</span>
        <StripeBtn :disabled="isFinishedStatus" @click="resetEverything"> Reset </StripeBtn>
      </div>
    </div>
    <BasePriceTable
      :prices="prices"
      :default-prices="defaultPrices"
      @update-price="updatePrice"
      @remove-price="removePrice"
    />
    <PageFooter @save-prices="savePrices" @add-price="addPrice" />
    <CarrierPriorityModal :id="modalTitle" :entity-id="assignmentId" type="assignment" />
  </div>
</template>

<script lang="ts">
import type { RouterLink } from '@/types/Components/Router'
import type { Assignment } from '@/types/Models/Assignment'
import type { BasePrice } from '@/types/Models/BasePrice'
import { storeToRefs } from 'pinia'
import {
  defineComponent,
  computed,
  onBeforeMount,
  ref,
  onMounted,
  defineAsyncComponent,
  type Ref,
  type ComputedRef,
} from 'vue'
import { useRoute, onBeforeRouteLeave } from 'vue-router'
import { CARRIER_PRIORITY_MODAL } from '@/const/ModalTypes'
import { ASSIGNMENT } from '@/const/PriceTypes'
import { none } from '@/services/Helpers'
import { openModal, registerModals } from '@/services/Modal'
import { addPrice, removePrice, resetEverything, savePrices, updatePrice } from '@/services/Prices/PriceActions'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { usePricesStore } from '@/store/prices'
import { STAFF_ASSIGNMENTS_DETAILS } from '@/types/router'
import StripeBtn from '@/views/Components/Elements/Buttons/StripeBtn.vue'
import CarrierPriorityModal from '@/views/Modals/Assignment/CarrierPriorityModal.vue'

export default defineComponent({
  name: 'AssignmentPrices',
  components: {
    StripeBtn,
    CarrierPriorityModal,
    PageFooter: defineAsyncComponent(() => import('@/views/Components/PageParts/PageFooter.vue')),
    BasePriceTable: defineAsyncComponent(() => import('@/views/Staff/BasePrice/BasePriceTable.vue')),
  },
  setup() {
    const assignmentSummaryStore = useAssignmentSummaryStore()
    const pricesStore = usePricesStore()
    const route = useRoute()

    const { isFinishedStatus } = storeToRefs(assignmentSummaryStore)

    const defaultPrices: Ref<BasePrice[]> = ref([])
    const modalTitle: string = CARRIER_PRIORITY_MODAL + 'assignment'

    const prices: ComputedRef<BasePrice[]> = computed(() => pricesStore.prices)
    const changedPrices: ComputedRef<BasePrice[]> = computed(() => pricesStore.changedPrices)
    const assignment: ComputedRef<Assignment | null> = computed(() => assignmentSummaryStore.assignment)
    const assignmentId: ComputedRef<string> = computed(() => String(route.params.id))

    const assignmentPage: ComputedRef<RouterLink> = computed(() => ({
      name: STAFF_ASSIGNMENTS_DETAILS,
      params: { id: assignmentId.value },
    }))

    onBeforeMount(() => {
      pricesStore.setType(ASSIGNMENT)
      pricesStore.setEntityId(assignmentId.value)
      pricesStore
        .fetchDefault(assignmentId.value)
        .then((res) => {
          defaultPrices.value = res
          pricesStore
            .fetch(assignmentId.value)
            .then((result) => {
              if (result.length === 0) {
                pricesStore.setPrices(defaultPrices.value)
              }
            })
            .catch(none)
        })
        .catch(none)
    })

    onMounted(() => {
      registerModals(modalTitle)
    })

    onBeforeRouteLeave((_to, _from) => {
      if (changedPrices.value.length > 0) {
        return new Promise((resolve) => {
          openModal('confirmWrap', {
            title: 'There are unsaved changes',
            message: 'Are you sure you want to cancel?',
            onConfirm: () => {
              pricesStore.reset().catch(none)
              resolve(true)
            },
            onCancel: () => {
              resolve(false)
            },
          })
        })
      }
      return true
    })

    return {
      prices,
      modalTitle,
      assignment,
      assignmentId,
      defaultPrices,
      assignmentPage,
      isFinishedStatus,
      addPrice,
      openModal,
      savePrices,
      updatePrice,
      removePrice,
      resetEverything,
    }
  },
})
</script>
